import Dialog from "../../General/ConfirmableDialog";
import React, {
    ChangeEvent,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";

interface IncidentProofUploadModalProps {
    open: boolean;
    onClose: () => void;
    incident: any;
}

const IncidentProofUploadModal: React.FC<IncidentProofUploadModalProps> = ({
    open,
    onClose,
    incident,
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [isDragActive, setIsDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { token } = useAuth();
    const queryClient = useQueryClient();

    const ACCEPTED_MIME_TYPES = useMemo(
        () => new Set(["image/jpeg", "image/png", "application/pdf"]),
        []
    );
    const ACCEPTED_EXTENSIONS = useMemo(
        () => [".jpeg", ".jpg", ".png", ".pdf"],
        []
    );

    const buildFileKey = useCallback(
        (file: File) => `${file.name}-${file.size}-${file.lastModified}`,
        []
    );

    const addFiles = useCallback(
        (incomingFiles: File[]) => {
            if (!incomingFiles.length) {
                return;
            }

            const acceptedFiles = incomingFiles.filter((file) => {
                if (file.type && ACCEPTED_MIME_TYPES.has(file.type)) {
                    return true;
                }

                const lowerName = file.name.toLowerCase();
                return ACCEPTED_EXTENSIONS.some((extension) =>
                    lowerName.endsWith(extension)
                );
            });

            if (!acceptedFiles.length) {
                setStatus("error");
                setErrorMessage("Unsupported file type. Please upload images or PDFs.");
                return;
            }

            setStatus("idle");
            setErrorMessage(null);

            setFiles((previous) => {
                const existingKeys = new Set(previous.map(buildFileKey));
                const deduplicated = acceptedFiles.filter((file) => {
                    const key = buildFileKey(file);
                    if (existingKeys.has(key)) {
                        return false;
                    }
                    existingKeys.add(key);
                    return true;
                });

                if (!deduplicated.length) {
                    return previous;
                }

                return [...previous, ...deduplicated];
            });
        },
        [ACCEPTED_EXTENSIONS, ACCEPTED_MIME_TYPES, buildFileKey]
    );

    const handleSnackbarClose = () => setStatus("idle");

    const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        addFiles(Array.from(event.target.files ?? []));
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setIsDragActive(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }
        setIsDragActive(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);
        const droppedFiles = Array.from(event.dataTransfer.files ?? []);
        addFiles(droppedFiles);
        event.dataTransfer.clearData();
    };

    const handleClickDropZone = () => {
        fileInputRef.current?.click();
    };

    const uploadMutation = useMutation({
        mutationFn: async () => {
            if (!files.length || !incident?.id) {
                throw new Error("No files selected or incident data is missing");
            }

            const formData = new FormData();
            files.forEach((file) => {
                formData.append("files", file);
            });

            setLoading(true);

            const response = await axios.post(
                `${apiVariable}/incident/uploadMultiple/${incident.id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token ?? ""}`,
                    },
                }
            );

            return response.data;
        },
        onSuccess: () => {
            setStatus("success");
            setLoading(false);
            setFiles([]);
            queryClient.invalidateQueries({
                queryKey: ["incidentProofs", incident.id],
            });
            onClose();
        },
        onError: (error: any) => {
            setStatus("error");
            setLoading(false);
            setErrorMessage(
                error?.response?.data || "Error occurred during file upload."
            );
        },
    });

    const handleSubmit = () => {
        uploadMutation.mutate();
    };

    const handleDialogClose = () => {
        setFiles([]);
        setIsDragActive(false);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Proofs for Incident #{incident?.id}</DialogTitle>
                <DialogContent>
                    <Box sx={{ marginBottom: 2 }}>
                        <FormControl fullWidth>
                            <Box
                                onClick={handleClickDropZone}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                sx={{
                                    border: "2px dashed",
                                    borderColor: isDragActive ? "primary.main" : "divider",
                                    borderRadius: 2,
                                    minHeight: { xs: 220, sm: 280 },
                                    p: { xs: 4, sm: 5 },
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: 1,
                                    boxSizing: "border-box",
                                    width: "100%",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: isDragActive ? "action.hover" : "transparent",
                                    transition: "border-color 0.2s ease-in-out, background-color 0.2s",
                                }}
                            >
                                <Typography variant="subtitle1" gutterBottom>
                                    Drag &amp; drop proofs here
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    or click to browse your files
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Accepted formats: JPG, PNG, PDF
                                </Typography>
                                <input
                                    hidden
                                    ref={fileInputRef}
                                    multiple
                                    type="file"
                                    accept="image/jpeg,image/png,application/pdf"
                                    onChange={handleFilesChange}
                                />
                            </Box>
                            {!!files.length && (
                                <Box mt={2} display="flex" flexDirection="column" gap={0.5}>
                                    {files.map((file) => (
                                        <Typography
                                            key={buildFileKey(file)}
                                            variant="body2"
                                            noWrap
                                            title={file.name}
                                        >
                                            {file.name}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary" variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : "Upload"}
                    </Button>
                </DialogActions>
            </Dialog>
            <StatusSnackbar
                status={status}
                successMessage="Files uploaded successfully!"
                errorMessage={errorMessage}
                handleClose={handleSnackbarClose}
            />
        </>
    );
};

export default IncidentProofUploadModal;